/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */

/*
 * Builds a completely self contained MapLibre style out of the country
 * outlines the app already ships (src/assets is vite's publicDir, so
 * countries-50m.json and countries-110m.json are served from the web root).
 *
 * Nothing here reaches the network: no tile server and no sprite sheet, and
 * the glyphs a symbol layer needs are generated ahead of time and served from
 * src/assets like everything else, so the desktop app still works offline.
 *
 * Live data draws through the same glyphs: GeoMap labels its points and their
 * observation counts with symbol layers of its own, which is what puts them in
 * the same collision index as the city names rather than on top of them.
 */

import { feature } from 'topojson-client';
import type { StyleSpecification } from 'maplibre-gl';

export type MapColors = {
    bg: string;
    fill: string;
    stroke: string;
    grat?: string;
    state?: string;
    city?: string;
};

export const COUNTRIES_50M = '/countries-50m.json';
export const COUNTRIES_110M = '/countries-110m.json';

/*
 * Both are Natural Earth, the same public domain source the country outlines
 * come from, so they add no license obligation.  scripts/build_basemap.sh
 * regenerates them.
 *
 * The states file holds admin-1 boundaries as lines rather than polygons, and
 * only the ones interior to a country: international borders were dropped at
 * build time precisely so this layer cannot double strike the outlines that
 * countries-*m.json is already drawing.  Cities are plain GeoJSON points that
 * carry Natural Earth's scalerank, which is what the zoom gate below reads.
 */
export const STATES_10M = '/states-10m.json';
export const CITIES_50M = '/cities-50m.json';

/*
 * Natural Earth ranks a place 0 (a world capital) through 10 (a minor town),
 * intended for exactly this kind of zoom gate.  The 50m set is bunched at the
 * low ranks though -- 27 cities at rank 0, but 606 at rank 4 -- so releasing
 * one rank per zoom level would quadruple the dots on screen in a single step.
 * Spreading each rank over rather more than a zoom level keeps that gradual.
 */
const CITY_RANK_ZOOM = 1.3;

/*
 * Glyphs are generated from Noto Sans by scripts/build_basemap.sh and served
 * out of src/assets, so labels cost no network round trip to anyone else and
 * the app stays usable offline.  Only the codepoint blocks the city names
 * actually use are shipped, and MapLibre fetches even those lazily.
 */
export const GLYPHS = '/glyphs/{fontstack}/{range}.pbf';
export const LABEL_FONT = 'Noto Sans Regular';

/*
 * The codepoint blocks scripts/build_basemap.sh actually builds: latin plus
 * its accented forms, and the punctuation block an en dash lives in.  The city
 * names were checked against that at build time, but a layer labelling live
 * data cannot be, and a character outside these blocks draws as a blank while
 * setting MapLibre chasing a glyph range that 404s.
 *
 * So live labels come through here first.  Undrawable characters are dropped
 * rather than the whole name, since what they usually are is a native spelling
 * trailing an otherwise latin one -- "Moonshot AI (月之暗面)" labels perfectly
 * well as "Moonshot AI" -- and the bracket or separator left stranded by the
 * removal goes with them.  A name with no latin in it at all cannot be drawn
 * and comes back empty for the caller to skip.
 */
const GLYPH_BLOCKS = [ [ 0, 767 ], [ 8192, 8447 ] ];

export function drawableLabel(text: string): string {
    let out = '';
    for (const ch of text) {
        const cp = ch.codePointAt(0) as number;
        out += GLYPH_BLOCKS.some(([ lo, hi ]) => cp >= lo && cp <= hi) ? ch : ' ';
    }
    out = out.replace(/\(\s*\)|\[\s*\]|\{\s*\}/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/^[\s,;:/\-]+|[\s,;:/\-]+$/g, '');
    // Punctuation on its own is not a label
    return /[A-Za-z0-9]/.test(out) ? out : '';
}

/*
 * Russia, Fiji and Antarctica have rings that step straight across the
 * antimeridian, so consecutive vertices jump ~360 degrees of longitude.  d3
 * used to clip that away while projecting, but MapLibre takes the coordinates
 * as given and fills the shortest path between them, which paints a wedge
 * clean across the northern hemisphere.
 *
 * Making each ring continuous fixes it: once a vertex crosses the seam every
 * following vertex is shifted by a whole turn, so a ring may legitimately run
 * past 180 (Russia ends up spanning roughly 19 to 190).  MapLibre draws the
 * overhang in the neighbouring world copy, which is exactly where it belongs.
 */
function unwrapRing(ring: number[][]): number[][] {
    if (ring.length < 2) {
        return ring;
    }
    const out = [ ring[0] ];
    let prev = ring[0][0];
    let offset = 0;
    for (let i = 1; i < ring.length; i++) {
        let lon = ring[i][0] + offset;
        if (lon - prev > 180) {
            offset -= 360;
            lon -= 360;
        } else if (lon - prev < -180) {
            offset += 360;
            lon += 360;
        }
        out.push([ lon, ring[i][1] ]);
        prev = lon;
    }
    return out;
}

function unwrapAntimeridian(collection: any): any {
    for (const f of collection.features || []) {
        const g = f.geometry;
        if (!g) {
            continue;
        }
        // Polygon and MultiLineString are both arrays of rings, so they share
        // a case; the state boundaries arrive as the line types
        if (g.type === 'LineString') {
            g.coordinates = unwrapRing(g.coordinates);
        } else if (g.type === 'Polygon' || g.type === 'MultiLineString') {
            g.coordinates = g.coordinates.map(unwrapRing);
        } else if (g.type === 'MultiPolygon') {
            g.coordinates = g.coordinates.map((poly: number[][][]) => poly.map(unwrapRing));
        }
    }
    return collection;
}

// These are static assets, so one fetch and conversion per URL serves every
// map in the app for the life of the window
const dataCache = new Map<string, Promise<any>>();

export function loadMapData(url: string): Promise<any> {
    let pending = dataCache.get(url);
    if (!pending) {
        pending = fetch(url)
            .then((r) => {
                if (!r.ok) {
                    throw new Error(`${url}: ${r.status} ${r.statusText}`);
                }
                return r.json();
            })
            .then((doc) => {
                // Boundaries ship as topology to share arcs between neighbours;
                // a bag of city points has no topology to share, so it is left
                // as GeoJSON and needs no conversion
                if (doc?.type !== 'Topology') {
                    return doc;
                }
                const key = Object.keys(doc.objects || {})[0];
                if (!key) {
                    throw new Error(`${url}: no topology objects`);
                }
                return unwrapAntimeridian(feature(doc, doc.objects[key]));
            })
            .catch((e) => {
                // Do not cache a failure, the next map should retry
                dataCache.delete(url);
                throw e;
            });
        dataCache.set(url, pending);
    }
    return pending;
}

/*
 * react-simple-maps drew a graticule for us; MapLibre has no equivalent, so
 * the grid is just another GeoJSON source.  Meridians need intermediate
 * vertices to stay smooth under reprojection, parallels do not, but they get
 * them anyway for the sake of one code path.
 */
function buildGraticule(step = 20): any {
    const features = [] as any[];
    const span = (from: number, to: number, by: number) => {
        const out = [] as number[];
        for (let v = from; v <= to; v += by) {
            out.push(v);
        }
        return out;
    };
    for (const lat of span(-80, 80, step)) {
        features.push({
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'LineString',
                coordinates: span(-180, 180, 10).map((lon) => [ lon, lat ])
            }
        });
    }
    for (const lon of span(-180, 180, step)) {
        features.push({
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'LineString',
                coordinates: span(-80, 80, 10).map((lat) => [ lon, lat ])
            }
        });
    }
    return { type: 'FeatureCollection', features };
}

export type BaseStyleData = {
    countries: any;
    // Omitted when a caller has those layers switched off
    states?: any;
    cities?: any;
};

export function buildBaseStyle(colors: MapColors, data: BaseStyleData,
                              graticule = true): StyleSpecification {
    const { countries, states, cities } = data;
    const sources: any = {
        countries: { type: 'geojson', data: countries }
    };
    const layers: any[] = [
        {
            id: 'background',
            type: 'background',
            paint: { 'background-color': colors.bg }
        },
        {
            id: 'country-fill',
            type: 'fill',
            source: 'countries',
            paint: { 'fill-color': colors.fill }
        }
    ];
    if (graticule && colors.grat) {
        sources.graticule = { type: 'geojson', data: buildGraticule() };
        layers.push({
            id: 'graticule',
            type: 'line',
            source: 'graticule',
            paint: { 'line-color': colors.grat, 'line-width': 0.5, 'line-opacity': 0.6 }
        });
    }
    // Drawn after the graticule so borders stay legible over the grid
    layers.push({
        id: 'country-line',
        type: 'line',
        source: 'countries',
        paint: { 'line-color': colors.stroke, 'line-width': 0.5 }
    });

    /*
     * State borders sit above the national ones rather than below.  The two
     * never coincide -- international borders were stripped from the states
     * file -- so nothing is hidden either way, and staying on top leaves the
     * slot beneath country-line free for callers that insert a highlight fill
     * there (FingerGeoMap does) without burying the state lines under it.
     *
     * Thinner and fainter than a national border at every zoom, but never
     * faded out: a panel showing the whole world once sits near zoom 1-2
     * depending on how wide it is, and state boundaries are wanted there.
     */
    if (states) {
        sources.states = { type: 'geojson', data: states };
        layers.push({
            id: 'state-line',
            type: 'line',
            source: 'states',
            paint: {
                'line-color': colors.state || colors.stroke,
                'line-width': 0.4,
                'line-opacity': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 0, 0.35, 3, 0.55 ]
            }
        });
    }

    /*
     * Both city layers share one gate, written as a filter rather than as a
     * paint opacity.  A filter takes zoom directly, and more to the point a
     * filtered out city takes no part in label collision, where one merely
     * faded to zero would still elbow a visible neighbour out of its place.
     */
    if (cities) {
        sources.cities = { type: 'geojson', data: cities };
        const shown = [ '<=', [ '*', [ 'get', 'scalerank' ], CITY_RANK_ZOOM ], [ 'zoom' ] ];
        layers.push({
            id: 'city-dot',
            type: 'circle',
            source: 'cities',
            filter: shown,
            paint: {
                'circle-color': colors.city || colors.stroke,
                'circle-radius': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 1, 1.2, 6, 2.5, 12, 4 ],
                'circle-opacity': 0.8
            }
        });
        /*
         * Left alone MapLibre drops any label that would overlap one already
         * placed, which is exactly what is wanted: the map stays legible at
         * every zoom without a label count having to be guessed here.  Two
         * settings earn their keep on top of that.  A variable anchor lets a
         * name try four sides of its dot before giving up, which fits more of
         * them into the same space, and sorting by rank decides collisions in
         * favour of the more important city rather than whichever one the
         * source happened to list first.
         */
        layers.push({
            id: 'city-label',
            type: 'symbol',
            source: 'cities',
            filter: shown,
            layout: {
                'text-field': [ 'get', 'name' ],
                'text-font': [ LABEL_FONT ],
                'text-size': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 2, 18, 8, 22 ],
                'text-variable-anchor': [ 'left', 'right', 'top', 'bottom' ],
                'text-radial-offset': 0.6,
                'text-justify': 'auto',
                'text-padding': 1,
                'symbol-sort-key': [ 'get', 'scalerank' ]
            },
            paint: {
                'text-color': colors.city || colors.stroke,
                // Knocking the map back out around each glyph keeps a name
                // readable where it crosses a border or a coastline
                'text-halo-color': colors.bg,
                'text-halo-width': 1,
                'text-halo-blur': 0.5
            }
        });
    }
    return { version: 8, glyphs: GLYPHS, sources, layers } as StyleSpecification;
}

export function mapColorsFromTheme(theme: any): MapColors {
    const c = theme?.colors || {};
    /*
     * No theme defines mapState or mapCity yet, and a fixed grey would fight
     * palettes like fbot's green on black.  Falling back to the country stroke
     * keeps the new layers on tone in every theme; they stay subordinate
     * through width and opacity rather than through colour.
     */
    return {
        bg: c.mapBg || '#11131a',
        fill: c.mapFill || '#1b2430',
        stroke: c.mapStroke || '#4b5563',
        grat: c.mapGrat || '#2f3b4a',
        state: c.mapState || c.mapStroke || '#4b5563',
        city: c.mapCity || c.mapStroke || '#4b5563'
    };
}
